import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar } from 'lucide-react';
import { renderEmojiOrIcon } from './Icons';
import { EmojiPicker } from './EmojiPicker';

// The Itinerary tab's Shortlist (DayDetail) mode's destination "hero" card —
// header, weather/nights bar. Schedule uses the slimmer DestinationStrip
// instead (see DestinationStrip.tsx). Truncated: the description and
// travellers row that used to sit below the weather/nights bar are gone.
// Weather and the nights line are caller-supplied (a rendered slot / a
// pre-formatted string) — Shortlist shows a destination-wide forecast range
// and a plain nights total.
export function DestinationHeroCard({
  dateLabel, name, flag, country, cityBg, accent,
  emoji, canEditEmoji, editingEmoji, onEditEmoji, onSelectEmoji, onCloseEmojiPicker,
  hasPrev, hasNext, onNavigateDest,
  weatherSlot, nightsLabel,
}: {
  dateLabel: string;
  name: string;
  flag: string;
  country: string;
  cityBg: string;
  accent: string;
  emoji: string;
  canEditEmoji?: boolean;
  editingEmoji?: boolean;
  onEditEmoji?: () => void;
  onSelectEmoji?: (emoji: string) => void;
  onCloseEmojiPicker?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onNavigateDest?: (dir: 'prev' | 'next') => void;
  weatherSlot: ReactNode;
  nightsLabel: string;
}) {
  return (
    <motion.div
      drag={(hasPrev || hasNext) ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.15}
      onDragEnd={(_, info) => {
        if ((info.offset.x < -60 || info.velocity.x < -500) && hasNext) onNavigateDest?.('next');
        else if ((info.offset.x > 60 || info.velocity.x > 500) && hasPrev) onNavigateDest?.('prev');
      }}
      // rounded-2xl, not 3xl: this is the only 24px radius left on a surface
      // this size, and against the ordinary 16px cards below it the card read
      // as a different material rather than the same one, larger.
      className={`rounded-2xl px-7 py-5 relative overflow-hidden border border-app-border/20 shadow-lg touch-pan-y ${(hasPrev || hasNext) ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      {/* The destination colour is its own layer rather than the card's
          background, at full strength: letting the page's drifting tint show
          through it made the card read as washed out beside the solid cards
          below. Text stays white, and the black scrim above holds the
          contrast. */}
      <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: cityBg }} />
      <div className="absolute inset-0 bg-black/35 rounded-2xl pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-64 h-64 blur-[100px] opacity-10 rounded-2xl pointer-events-none"
        style={{ backgroundColor: accent }}
      />

      {/* items-center (was items-stretch): the emoji is the same visual
          weight as the destination name, so it should land centred against
          the name+country block, not pinned to the top beside the small
          date label above them. */}
      <div className="relative z-10 flex justify-between items-center mb-6">
        <div className="drop-shadow-lg">
          <div className="text-white/80 text-[11px] font-black tracking-widest uppercase mb-2">{dateLabel}</div>
          <h2 className="text-white font-serif text-5xl font-bold leading-tight tracking-tighter">{name}</h2>
          <div className="flex items-center gap-2 mt-2 text-white/80 text-[11px] font-bold uppercase tracking-widest">
            <span className="text-xl leading-none">{flag}</span>
            <span>{country}</span>
          </div>
        </div>
        <div className="flex flex-col items-end relative z-30">
          <div className="text-6xl filter drop-shadow-xl select-none relative overflow-visible">
            <button aria-label="Change destination emoji"
              onClick={onEditEmoji}
              disabled={!canEditEmoji}
              className={canEditEmoji ? 'cursor-pointer hover:scale-110 transition-transform active:scale-95' : 'cursor-default'}
            >
              {renderEmojiOrIcon(emoji, 64)}
            </button>
            <AnimatePresence>
              {editingEmoji && onSelectEmoji && onCloseEmojiPicker && (
                <EmojiPicker currentEmoji={emoji} onSelect={onSelectEmoji} onClose={onCloseEmojiPicker} />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-between text-white/90 text-[11px] font-bold uppercase tracking-wider border-t border-white/10 pt-4 mt-6">
        {weatherSlot}
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 opacity-70" />
          <span>{nightsLabel}</span>
        </div>
      </div>
    </motion.div>
  );
}
